// =============================================================================
// BarreOnglets.js — la barre de navigation basse (téléphone, iPad en portrait).
//
// Pourquoi (retour Pierre, 16/09/2026) : « les éléments s'affichent comme si on
// était sur un ordinateur alors qu'on est sur mobile. Ce n'est pas ce que
// j'appelle de la responsivité. » Une application mobile se reconnaît d'abord à
// ça — des destinations permanentes, à portée de pouce, en bas de l'écran.
//
// CINQ PORTES, PAS UN MENU : Accueil, Modules (la feuille des modules), le
// module courant (ou, sur l'accueil, le dernier visité), Rechercher (la
// palette), Moi (la feuille personnelle). Aucune entrée de navigation n'est
// écrite ici — tout vient de buildNavModules(), règle d'or du dépôt (CLAUDE.md
// §« Menu desktop = menu mobile », vérifiée par src/navConstants.test.js).
//
// Lot L6 « Coquille et transitions » (28/09/2026, synthèse §6.3) :
//   • barre OPAQUE (plus de verre sur un élément fixe au doigt) ;
//   • PASTILLE en tuile (52 × 32, rayon 12) derrière l'onglet actif, qui GLISSE
//     d'un onglet à l'autre (FLIP, --spring-smooth 380 ms ; rien au clavier ni
//     sous mouvement réduit), à la teinte du module ouvert ;
//   • glyphe à 90 % à l'appui ;
//   • pastille des messages non lus RÉELLEMENT branchée sur « Moi » (la
//     messagerie y vit, décision n° 8 ; la cloche de l'en-tête porte les
//     notifications).
//
// Lot L11r7 (01/10/2026) : libellés au plancher de 12 px (--fs-micro), JAMAIS
// tronqués, dans les trois tailles de texte et tous les modules. Quand les cinq
// libellés entiers ne tiennent pas, ajusterBarre() descend des paliers, un à un
// (« Chercher », module sur deux lignes, marges serrées, échelle normale) :
// règles et mesures dans coquilleStyles.js.
//
// PORTÉE PAR createPortal : le header porte un `backdrop-filter`, ce qui fait
// de lui le bloc contenant de ses descendants `position:fixed`.
// zIndex 950 : au-dessus du contenu, SOUS tous les calques (feuilles, modales).
// =============================================================================
import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { navTexte, NAV_COLORS, compteModule } from "./navConstants";
import { Icone } from "./icones";
import { mouvementReduit, saisieClavierRecente } from "./mouvement";
import { glisserDepuis, rectHomologue, optsCoquille } from "./navigationCoquille";

// `compte` : ce qui attend un acte dans un module ({ n, libelle }, compteModule
// de navConstants, le même chiffre que le rail et la feuille « Modules »),
// exact ; `pastille` : les messages non lus de « Moi », plafonnés à « 9+ ».
// `court` : libellé court d'une porte FIXE, montré au seul palier « court »
// (le nom accessible reste `libelle`, entier). `affiche` : le libellé TOUJOURS
// montré d'un module qui déclare un nom court (`court` de « Mon espace
// salarié », 26/10/2026 : « Mon espace » ; le nom entier reste le nom
// accessible, le fil et le titre de la page) : le nom entier tassait les
// cinq onglets dès 390 px.
function Onglet({ cle, icone, libelle, court = null, affiche = null, actif, teinte, pastille, compte = null, onClick }) {
  const chiffre = compte ? compte.n : pastille > 0 ? (pastille > 9 ? "9+" : pastille) : null;
  return (
    <button type="button" onClick={onClick} className="cq-onglet" data-onglet={cle}
      aria-current={actif ? "page" : undefined}
      aria-label={compte ? `${libelle}, ${compte.libelle}`
        : pastille > 0 ? `${libelle}, ${pastille} message${pastille > 1 ? "s" : ""} non lu${pastille > 1 ? "s" : ""}` : libelle}
      style={teinte ? { "--cq-h": teinte, "--cq-hi": navTexte(teinte) } : undefined}>
      {actif && <span className="cq-onglet-pastille" aria-hidden="true" />}
      <span className="cq-onglet-glyphe">
        <Icone name={icone} size={24} />
        {chiffre !== null && <span className="cq-onglet-compteur" aria-hidden="true">{chiffre}</span>}
      </span>
      <span className="cq-onglet-libelle">
        {affiche || (court ? <><span className="cq-libelle-long">{libelle}</span><span className="cq-libelle-court">{court}</span></> : libelle)}
      </span>
    </button>
  );
}

// Paliers de la barre, du plus doux au plus fort (coquilleStyles.js) : chacun
// s'ajoute aux précédents, et la barre prend le premier où tout tient.
export const PALIERS_BARRE = ["court", "deuxLignes", "serre", "echelle"];

const deborde = (nav) => nav.scrollWidth > nav.clientWidth + 0.5
  || [...nav.querySelectorAll(".cq-onglet-libelle")].some((l) => l.scrollWidth > l.clientWidth + 0.5);

/** Pose sur la barre le premier palier où les cinq libellés tiennent ENTIERS,
 *  et publie <html data-onglets-lignes="2"> quand le libellé du module prend
 *  réellement deux lignes : la barre grandit alors de 12 px (--h-onglets), et
 *  seulement alors. Renvoie le nombre de paliers retenus. */
export function ajusterBarre(nav) {
  if (!nav) return 0;
  PALIERS_BARRE.forEach((p) => { delete nav.dataset[p]; });
  let n = 0;
  while (n < PALIERS_BARRE.length && deborde(nav)) { nav.dataset[PALIERS_BARRE[n]] = ""; n += 1; }
  // Un palier plus doux que rend inutile un plus fort est retiré : quand la
  // seconde ligne du module suffit, « Rechercher » reste entier.
  for (const p of PALIERS_BARRE.slice(0, Math.max(0, n - 1))) {
    delete nav.dataset[p];
    if (deborde(nav)) nav.dataset[p] = "";
  }
  const mod = nav.querySelector('[data-onglet="module"] .cq-onglet-libelle');
  const ref = nav.querySelector('[data-onglet="accueil"] .cq-onglet-libelle');
  const deux = "deuxLignes" in nav.dataset && !!mod && !!ref
    && mod.getBoundingClientRect().height > ref.getBoundingClientRect().height * 1.5;
  const html = document.documentElement;
  if (deux) html.dataset.ongletsLignes = "2"; else delete html.dataset.ongletsLignes;
  return PALIERS_BARRE.filter((p) => p in nav.dataset).length;
}

/** Publie <html data-onglets="1"> tant que la barre est affichée : le socle
 *  mobile en tire --h-onglets (réserve du bas de page), l'en-tête sa forme
 *  « doigt » et la palette sa place en haut d'écran. Un composant et non un
 *  crochet : le rendu d'App a des retours anticipés. */
export function AttributOnglets({ actif }) {
  useEffect(() => {
    const html = document.documentElement;
    if (actif) html.dataset.onglets = "1"; else delete html.dataset.onglets;
    return () => { delete document.documentElement.dataset.onglets; };
  }, [actif]);
  return null;
}

/**
 * @param {string}   view          vue courante
 * @param {Function} onNav         navTo
 * @param {object|null} module     module de l'onglet contextuel (courant, ou
 *        à défaut le dernier visité — jamais null quand un menu existe)
 * @param {boolean}  moduleActif   vrai si l'on se trouve DANS ce module
 * @param {Function} onModules     ouvre la feuille « Modules »
 * @param {Function} onSousMenu    ouvre la porte du module de l'onglet
 * @param {Function} onRechercher  ouvre la palette
 * @param {Function} onMoi         ouvre la feuille « Moi »
 * @param {number}   nonLus        messages non lus (pastille de « Moi »)
 * @param {object[]} [modules]     les modules du menu (buildNavModules) : le
 *        chiffre de ce qui attend un acte (« 3 demandes d'engagement à
 *        valider ») sur l'onglet du module et, pour tous, sur « Modules »
 * @param {string}   [ongletActif] « modules », « rechercher », « moi » quand
 *        leur surface est ouverte ; sinon déduit de la vue
 * @param {Element}  conteneur     cible du portail (défaut document.body).
 *        Paramétrable pour l'aperçu de développement, qui monte les composants
 *        dans un <iframe> : sans cela le portail viserait le document parent.
 */
export default function BarreOnglets({
  view, onNav, module, moduleActif = false, onModules, onSousMenu,
  onRechercher, onMoi, nonLus = 0, ongletActif = null,
  conteneur = null, modules = [],
}) {
  const barre = useRef(null);
  // L'onglet contextuel. `module` est TOUJOURS un vrai module : sur l'accueil,
  // où `moduleForView` ne renvoie rien, App.jsx fournit le dernier module
  // visité (ou le premier du menu). `moduleActif` dit seulement si l'on s'y
  // trouve en ce moment — c'est lui qui allume l'onglet, pas sa présence.
  // Avant ce partage, l'onglet affichait un carré noir intitulé « Aucun »
  // dès qu'on était sur l'accueil (signalé par Pierre le 17/09/2026).
  const aModule = !!module;
  const actif = ongletActif
    || (view === "ma-semaine" ? "accueil"
      : moduleActif ? "module"
        : view === "mon-profil" ? "moi" : null);
  const precedent = useRef(actif);

  // La pastille glisse de l'ancien onglet au nouveau (régime spatial).
  useLayoutEffect(() => {
    const avant = precedent.current;
    precedent.current = actif;
    if (!avant || !actif || avant === actif || !barre.current) return;
    if (mouvementReduit() || saisieClavierRecente()) return;
    const depuis = barre.current.querySelector(`[data-onglet="${avant}"]`);
    const pastille = barre.current.querySelector(".cq-onglet-pastille");
    if (depuis && pastille) glisserDepuis(pastille, rectHomologue(depuis, pastille), { axe: "x" });
  }, [actif]);

  // Paliers : avant la peinture quand le libellé du module change, puis à
  // chaque changement de LARGEUR de la barre (rotation, taille du texte, qui
  // pose un zoom) et à l'arrivée des polices. Une image plus tard : la barre
  // change de hauteur au palier « deux lignes », et l'observateur ne doit pas
  // se relancer dans sa propre notification.
  const libelleModule = aModule ? (module.court || module.label) : "";
  useLayoutEffect(() => { ajusterBarre(barre.current); }, [libelleModule]);
  useEffect(() => {
    const nav = barre.current;
    if (!nav) return undefined;
    let image = 0, largeur = -1;
    const raf = typeof requestAnimationFrame === "function" ? requestAnimationFrame : (f) => setTimeout(f, 16);
    const annuler = typeof cancelAnimationFrame === "function" ? cancelAnimationFrame : clearTimeout;
    const planifier = () => { annuler(image); image = raf(() => ajusterBarre(nav)); };
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver((entrees) => {
      const l = entrees[0] ? entrees[0].contentRect.width : -1;
      if (l !== largeur) { largeur = l; planifier(); }
    }) : null;
    if (ro) ro.observe(nav);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(planifier, () => {});
    return () => { annuler(image); if (ro) ro.disconnect(); delete document.documentElement.dataset.ongletsLignes; };
  }, []);

  // Chiffres lus sur les modules DU RENDU (celui de l'onglet peut venir d'une
  // visite antérieure). Le module de l'onglet porte le sien ; « Modules »,
  // d'où l'on atteint chaque tuile, la somme des AUTRES : jamais deux fois
  // le même chiffre dans la barre.
  const visibles = (modules || []).filter((m) => m && m.show !== false);
  const moduleFrais = (module && visibles.find((m) => m.key === module.key)) || module;
  const compteOnglet = moduleFrais ? compteModule(moduleFrais) : null;
  const comptes = visibles.filter((m) => !aModule || m.key !== module.key).map(compteModule).filter(Boolean);
  const compteModules = comptes.length
    ? { n: comptes.reduce((t, c) => t + c.n, 0), libelle: comptes.map((c) => c.libelle).join(", ") } : null;

  const cible = conteneur || (typeof document !== "undefined" ? document.body : null);
  if (!cible) return null;

  return createPortal(
    <nav ref={barre} className="cq-onglets" aria-label="Navigation principale">
      <Onglet cle="accueil" icone="home" libelle="Accueil" actif={actif === "accueil"}
        onClick={(e) => onNav("ma-semaine", null, optsCoquille(e))} />
      <Onglet cle="modules" icone="layout-grid" libelle="Modules" actif={actif === "modules"} compte={compteModules} onClick={onModules} />
      {aModule && (
        <Onglet cle="module"
          icone={module.icon || module.accueil?.icon || module.items?.[0]?.icon || "layout-grid"}
          libelle={module.label} affiche={module.court || null}
          actif={actif === "module"} teinte={module.color || NAV_COLORS.pilotage}
          compte={compteOnglet} onClick={onSousMenu} />
      )}
      <Onglet cle="rechercher" icone="search" libelle="Rechercher" court="Chercher" actif={actif === "rechercher"} onClick={onRechercher} />
      <Onglet cle="moi" icone="user" libelle="Moi" actif={actif === "moi"} pastille={nonLus} onClick={onMoi} />
    </nav>,
    cible
  );
}
